// =============================================================================
// WTF Amplify CRM - Frontend Telemetry System
// =============================================================================
//
// Automatically captures frontend events and sends them to the backend in batches.
// Uses navigator.sendBeacon() for reliable delivery, falls back to fetch().
// This module NEVER crashes the app - all operations are wrapped in try/catch.
//
// Usage:
//   import telemetry from '@/lib/telemetry';
//   telemetry.init();
//   telemetry.setUser({ id: '...', name: '...', role: 'CEO' });
//   telemetry.trackEvent('feature', 'pipeline_filter_applied', { stage: 'WON' });
//
// =============================================================================

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TelemetryEvent {
  id: string;
  type: string;
  action: string;
  timestamp: string;
  sessionId: string;
  userId?: string;
  userName?: string;
  userRole?: string;
  pageUrl: string;
  data?: Record<string, unknown>;
  breadcrumbs?: Breadcrumb[];
}

export interface Breadcrumb {
  type: string;
  message: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

interface PerformanceTimings {
  dnsLookup: number;
  tcpConnect: number;
  ttfb: number;
  contentDownload: number;
  domInteractive: number;
  domComplete: number;
  loadComplete: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FLUSH_INTERVAL_MS = 10_000; // Flush every 10 seconds
const MAX_BATCH_SIZE = 50; // Flush when 50 events accumulated
const MAX_BREADCRUMBS = 25; // Keep last 25 breadcrumbs
const MAX_STRING_LENGTH = 500; // Truncate strings to prevent huge payloads
const MAX_TEXT_CONTENT_LENGTH = 100; // Truncate click text content
const MAX_PAYLOAD_BYTES = 64 * 1024; // 64KB max payload per batch
const HEARTBEAT_INTERVAL_MS = 60_000; // Heartbeat every 60 seconds
const LONG_TASK_THRESHOLD_MS = 200; // Report tasks longer than 200ms
const MAX_BUFFER_SIZE = 500; // Drop oldest events if buffer exceeds this

// Telemetry endpoint path (appended to API base URL)
const TELEMETRY_PATH = '/telemetry/events';

// Interactive element selectors for click tracking
const INTERACTIVE_SELECTORS = [
  'button',
  'a',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[data-telemetry]',
];

// Sensitive field names to never capture
const SENSITIVE_FIELDS = new Set([
  'password',
  'passwd',
  'secret',
  'token',
  'credit_card',
  'creditcard',
  'card_number',
  'cardnumber',
  'cvv',
  'cvc',
  'ssn',
  'social_security',
  'pin',
]);

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to manual generation
  }
  // Fallback UUID v4 generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function truncate(str: unknown, maxLength: number = MAX_STRING_LENGTH): string {
  if (str === null || str === undefined) return '';
  const s = String(str);
  if (s.length <= maxLength) return s;
  return s.slice(0, maxLength) + '...';
}

function safeTimestamp(): string {
  try {
    return new Date().toISOString();
  } catch {
    return String(Date.now());
  }
}

function getByteSize(str: string): number {
  try {
    return new Blob([str]).size;
  } catch {
    // Rough estimate: each char is ~1 byte for ASCII, ~3 for UTF-8
    return str.length * 2;
  }
}

function getApiBaseUrl(): string {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5000/api';
}

function isSensitiveField(name: string): boolean {
  const lower = name.toLowerCase().replace(/[-_\s]/g, '');
  return SENSITIVE_FIELDS.has(lower);
}

function sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (isSensitiveField(key)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      sanitized[key] = truncate(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = truncate(JSON.stringify(value));
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

// ---------------------------------------------------------------------------
// Telemetry Class
// ---------------------------------------------------------------------------

class Telemetry {
  private events: TelemetryEvent[] = [];
  private breadcrumbs: Breadcrumb[] = [];
  private sessionId: string = '';
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private userId?: string;
  private userName?: string;
  private userRole?: string;
  private initialized: boolean = false;
  private destroying: boolean = false;

  // Listener references for cleanup
  private boundOnError: ((event: ErrorEvent) => void) | null = null;
  private boundOnUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;
  private boundOnClick: ((event: MouseEvent) => void) | null = null;
  private boundOnSubmit: ((event: SubmitEvent) => void) | null = null;
  private boundOnPopState: (() => void) | null = null;
  private boundOnVisibilityChange: (() => void) | null = null;
  private boundOnOnline: (() => void) | null = null;
  private boundOnOffline: (() => void) | null = null;
  private boundOnBeforeUnload: (() => void) | null = null;
  private performanceObserver: PerformanceObserver | null = null;

  // Original pushState/replaceState for monkey-patching
  private originalPushState: typeof window.history.pushState | null = null;
  private originalReplaceState: typeof window.history.replaceState | null = null;

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  init(): void {
    try {
      if (this.initialized) return;
      if (typeof window === 'undefined') return;

      this.initialized = true;
      this.destroying = false;

      // Session ID: reuse from sessionStorage or generate new
      const storedSessionId = sessionStorage.getItem('telemetry_session_id');
      if (storedSessionId) {
        this.sessionId = storedSessionId;
      } else {
        this.sessionId = generateId();
        sessionStorage.setItem('telemetry_session_id', this.sessionId);
      }

      // Set up all automatic capture
      this.setupErrorTracking();
      this.setupClickTracking();
      this.setupFormTracking();
      this.setupPageViewTracking();
      this.setupPerformanceTracking();
      this.setupSessionTracking();

      // Start flush interval
      this.flushInterval = setInterval(() => {
        this.flush();
      }, FLUSH_INTERVAL_MS);

      // Start heartbeat
      this.heartbeatInterval = setInterval(() => {
        this.sendHeartbeat();
      }, HEARTBEAT_INTERVAL_MS);

      // Flush on page unload
      this.boundOnBeforeUnload = () => this.flush();
      window.addEventListener('beforeunload', this.boundOnBeforeUnload);

      // Track initial page view
      this.trackPageView();

      // Track initial performance after page load
      if (document.readyState === 'complete') {
        this.capturePageLoadTimings();
      } else {
        window.addEventListener('load', () => this.capturePageLoadTimings(), { once: true });
      }
    } catch (e) {
      // Telemetry must never crash the app
      console.warn('[Telemetry] Init failed:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // User Management
  // ---------------------------------------------------------------------------

  setUser(user: { id: string; name: string; role: string }): void {
    try {
      this.userId = user.id;
      this.userName = truncate(user.name, 100);
      this.userRole = user.role;

      this.trackEvent('session', 'user_identified', {
        userId: user.id,
        userRole: user.role,
      });
    } catch (e) {
      console.warn('[Telemetry] setUser failed:', e);
    }
  }

  clearUser(): void {
    try {
      this.trackEvent('session', 'user_cleared');
      this.userId = undefined;
      this.userName = undefined;
      this.userRole = undefined;
    } catch (e) {
      console.warn('[Telemetry] clearUser failed:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // Manual Event Tracking
  // ---------------------------------------------------------------------------

  trackEvent(type: string, action: string, data?: Record<string, unknown>): void {
    try {
      if (this.destroying) return;

      const event: TelemetryEvent = {
        id: generateId(),
        type: truncate(type, 50),
        action: truncate(action, 100),
        timestamp: safeTimestamp(),
        sessionId: this.sessionId,
        userId: this.userId,
        userName: this.userName,
        userRole: this.userRole,
        pageUrl: truncate(window.location.href, MAX_STRING_LENGTH),
        data: data ? sanitizeData(data) : undefined,
      };

      this.addEvent(event);

      // Also add to breadcrumbs
      this.addBreadcrumb(type, action, data);
    } catch (e) {
      console.warn('[Telemetry] trackEvent failed:', e);
    }
  }

  trackError(error: Error, context?: Record<string, unknown>): void {
    try {
      if (this.destroying) return;

      const event: TelemetryEvent = {
        id: generateId(),
        type: 'error',
        action: 'manual_error',
        timestamp: safeTimestamp(),
        sessionId: this.sessionId,
        userId: this.userId,
        userName: this.userName,
        userRole: this.userRole,
        pageUrl: truncate(window.location.href, MAX_STRING_LENGTH),
        data: {
          errorType: error.name || 'Error',
          message: truncate(error.message),
          stack: truncate(error.stack || '', MAX_STRING_LENGTH),
          ...(context ? sanitizeData(context) : {}),
        },
        breadcrumbs: [...this.breadcrumbs],
      };

      this.addEvent(event);
    } catch (e) {
      console.warn('[Telemetry] trackError failed:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // Flush & Send
  // ---------------------------------------------------------------------------

  flush(): void {
    try {
      if (this.events.length === 0) return;

      // Take all events from the buffer
      const eventsToSend = this.events.splice(0);
      this.sendBatch(eventsToSend);
    } catch (e) {
      console.warn('[Telemetry] flush failed:', e);
    }
  }

  private sendBatch(events: TelemetryEvent[]): void {
    try {
      if (events.length === 0) return;

      const endpoint = getApiBaseUrl() + TELEMETRY_PATH;

      // Split into chunks if payload is too large
      const batches = this.splitIntoBatches(events);

      for (const batch of batches) {
        const payload = JSON.stringify({
          events: batch,
          sessionId: this.sessionId,
          timestamp: safeTimestamp(),
        });

        // Always use fetch (with keepalive) so we can include the Authorization header.
        // sendBeacon cannot send custom headers and the backend requires authentication.
        this.sendViaFetch(endpoint, payload, batch);
      }
    } catch (e) {
      console.warn('[Telemetry] sendBatch failed:', e);
    }
  }

  private sendViaFetch(
    endpoint: string,
    payload: string,
    originalEvents: TelemetryEvent[]
  ): void {
    try {
      // Get auth token for the request
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      fetch(endpoint, {
        method: 'POST',
        headers,
        body: payload,
        keepalive: true, // Allows request to outlive the page
      }).catch(() => {
        // On failure, put events back into the buffer for retry
        // But respect the buffer size limit
        if (this.events.length + originalEvents.length <= MAX_BUFFER_SIZE) {
          this.events.unshift(...originalEvents);
        }
      });
    } catch {
      // If fetch throws synchronously, try to buffer events back
      if (this.events.length + originalEvents.length <= MAX_BUFFER_SIZE) {
        this.events.unshift(...originalEvents);
      }
    }
  }

  private splitIntoBatches(events: TelemetryEvent[]): TelemetryEvent[][] {
    const batches: TelemetryEvent[][] = [];
    let currentBatch: TelemetryEvent[] = [];
    let currentSize = 0;

    // Overhead for the wrapper JSON: {"events":[],"sessionId":"...","timestamp":"..."}
    const wrapperOverhead = 100;

    for (const event of events) {
      const eventJson = JSON.stringify(event);
      const eventSize = getByteSize(eventJson);

      // If adding this event exceeds the max, start a new batch
      if (
        currentBatch.length > 0 &&
        currentSize + eventSize + wrapperOverhead > MAX_PAYLOAD_BYTES
      ) {
        batches.push(currentBatch);
        currentBatch = [];
        currentSize = 0;
      }

      currentBatch.push(event);
      currentSize += eventSize;
    }

    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }

    return batches;
  }

  // ---------------------------------------------------------------------------
  // Internal: Event Buffer & Breadcrumbs
  // ---------------------------------------------------------------------------

  private addEvent(event: TelemetryEvent): void {
    this.events.push(event);

    // Drop oldest events if buffer is too large
    if (this.events.length > MAX_BUFFER_SIZE) {
      this.events.splice(0, this.events.length - MAX_BUFFER_SIZE);
    }

    // Auto-flush when batch size is reached
    if (this.events.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  private addBreadcrumb(
    type: string,
    message: string,
    data?: Record<string, unknown>
  ): void {
    this.breadcrumbs.push({
      type: truncate(type, 50),
      message: truncate(message, 200),
      timestamp: safeTimestamp(),
      data: data ? sanitizeData(data) : undefined,
    });

    // Keep only the last N breadcrumbs
    if (this.breadcrumbs.length > MAX_BREADCRUMBS) {
      this.breadcrumbs.splice(0, this.breadcrumbs.length - MAX_BREADCRUMBS);
    }
  }

  // ---------------------------------------------------------------------------
  // 1. JS Error Tracking
  // ---------------------------------------------------------------------------

  private setupErrorTracking(): void {
    try {
      // Global error handler
      this.boundOnError = (event: ErrorEvent) => {
        try {
          const errorEvent: TelemetryEvent = {
            id: generateId(),
            type: 'error',
            action: 'uncaught_error',
            timestamp: safeTimestamp(),
            sessionId: this.sessionId,
            userId: this.userId,
            userName: this.userName,
            userRole: this.userRole,
            pageUrl: truncate(window.location.href, MAX_STRING_LENGTH),
            data: {
              errorType: event.error?.name || 'Error',
              message: truncate(event.message),
              stack: truncate(event.error?.stack || '', MAX_STRING_LENGTH),
              filename: truncate(event.filename || '', MAX_STRING_LENGTH),
              lineno: event.lineno,
              colno: event.colno,
            },
            breadcrumbs: [...this.breadcrumbs],
          };
          this.addEvent(errorEvent);
        } catch {
          // Swallow - never crash from telemetry
        }
      };
      window.addEventListener('error', this.boundOnError);

      // Unhandled promise rejection handler
      this.boundOnUnhandledRejection = (event: PromiseRejectionEvent) => {
        try {
          let message = 'Unknown rejection';
          let stack = '';

          if (event.reason instanceof Error) {
            message = event.reason.message;
            stack = event.reason.stack || '';
          } else if (typeof event.reason === 'string') {
            message = event.reason;
          } else if (event.reason) {
            message = truncate(JSON.stringify(event.reason));
          }

          const errorEvent: TelemetryEvent = {
            id: generateId(),
            type: 'error',
            action: 'unhandled_rejection',
            timestamp: safeTimestamp(),
            sessionId: this.sessionId,
            userId: this.userId,
            userName: this.userName,
            userRole: this.userRole,
            pageUrl: truncate(window.location.href, MAX_STRING_LENGTH),
            data: {
              errorType: 'UnhandledPromiseRejection',
              message: truncate(message),
              stack: truncate(stack, MAX_STRING_LENGTH),
            },
            breadcrumbs: [...this.breadcrumbs],
          };
          this.addEvent(errorEvent);
        } catch {
          // Swallow
        }
      };
      window.addEventListener('unhandledrejection', this.boundOnUnhandledRejection);
    } catch (e) {
      console.warn('[Telemetry] setupErrorTracking failed:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // 2. API Call Tracking (via fetch monkey-patch)
  // ---------------------------------------------------------------------------
  //
  // NOTE: The app uses plain fetch() for API calls (not axios).
  // We install a global fetch wrapper to capture API metadata.
  // We intentionally do NOT import from api.ts to avoid circular dependencies.
  //
  // The wrapper only tracks calls to the API base URL, not all fetch calls.
  // ---------------------------------------------------------------------------

  private setupApiTracking(): void {
    // API tracking is handled externally via the patchFetch() function below
    // to avoid issues with the fetch wrapper referencing 'this' during init.
    // See patchFetch() at the bottom of this file.
  }

  /**
   * Called by the patched fetch to record an API call.
   * This is intentionally public so the fetch wrapper can call it.
   */
  recordApiCall(data: {
    method: string;
    url: string;
    status: number;
    duration: number;
    ok: boolean;
    errorMessage?: string;
  }): void {
    try {
      if (this.destroying) return;

      const action = data.ok ? 'api_success' : 'api_error';

      const event: TelemetryEvent = {
        id: generateId(),
        type: 'api',
        action,
        timestamp: safeTimestamp(),
        sessionId: this.sessionId,
        userId: this.userId,
        userName: this.userName,
        userRole: this.userRole,
        pageUrl: truncate(window.location.href, MAX_STRING_LENGTH),
        data: {
          method: data.method,
          url: truncate(data.url, MAX_STRING_LENGTH),
          status: data.status,
          duration: Math.round(data.duration),
          ok: data.ok,
          ...(data.errorMessage ? { errorMessage: truncate(data.errorMessage) } : {}),
        },
      };

      this.addEvent(event);
      this.addBreadcrumb('api', `${data.method} ${truncate(data.url, 100)} -> ${data.status}`);
    } catch {
      // Swallow
    }
  }

  // ---------------------------------------------------------------------------
  // 3. Page View Tracking
  // ---------------------------------------------------------------------------

  private setupPageViewTracking(): void {
    try {
      // Track popstate (browser back/forward)
      this.boundOnPopState = () => {
        this.trackPageView();
      };
      window.addEventListener('popstate', this.boundOnPopState);

      // Monkey-patch pushState and replaceState
      this.originalPushState = window.history.pushState.bind(window.history);
      this.originalReplaceState = window.history.replaceState.bind(window.history);

      const self = this;

      window.history.pushState = function (...args: Parameters<typeof window.history.pushState>) {
        const result = self.originalPushState!(...args);
        try {
          self.trackPageView();
        } catch {
          // Swallow
        }
        return result;
      };

      window.history.replaceState = function (...args: Parameters<typeof window.history.replaceState>) {
        const result = self.originalReplaceState!(...args);
        try {
          self.trackPageView();
        } catch {
          // Swallow
        }
        return result;
      };
    } catch (e) {
      console.warn('[Telemetry] setupPageViewTracking failed:', e);
    }
  }

  private trackPageView(): void {
    try {
      this.trackEvent('navigation', 'page_view', {
        url: truncate(window.location.href, MAX_STRING_LENGTH),
        path: truncate(window.location.pathname, MAX_STRING_LENGTH),
        hash: truncate(window.location.hash, MAX_STRING_LENGTH),
        referrer: truncate(document.referrer, MAX_STRING_LENGTH),
        title: truncate(document.title, MAX_STRING_LENGTH),
      });
    } catch {
      // Swallow
    }
  }

  // ---------------------------------------------------------------------------
  // 4. User Click Tracking
  // ---------------------------------------------------------------------------

  private setupClickTracking(): void {
    try {
      this.boundOnClick = (event: MouseEvent) => {
        try {
          const target = event.target as HTMLElement;
          if (!target) return;

          // Find the nearest interactive element
          const interactiveEl = this.findInteractiveElement(target);
          if (!interactiveEl) return;

          const tagName = interactiveEl.tagName.toLowerCase();
          const id = interactiveEl.id || undefined;
          const className = truncate(interactiveEl.className, 200);
          const textContent = truncate(
            interactiveEl.textContent?.trim() || '',
            MAX_TEXT_CONTENT_LENGTH
          );
          const ariaLabel = interactiveEl.getAttribute('aria-label') || undefined;
          const dataTestId = interactiveEl.getAttribute('data-testid') || undefined;
          const dataTelemetry = interactiveEl.getAttribute('data-telemetry') || undefined;
          const href =
            tagName === 'a'
              ? truncate((interactiveEl as HTMLAnchorElement).href, MAX_STRING_LENGTH)
              : undefined;
          const type =
            tagName === 'input' || tagName === 'button'
              ? (interactiveEl as HTMLInputElement).type
              : undefined;

          this.trackEvent('interaction', 'click', {
            tagName,
            ...(id ? { id } : {}),
            ...(className ? { className } : {}),
            ...(textContent ? { text: textContent } : {}),
            ...(ariaLabel ? { ariaLabel: truncate(ariaLabel, MAX_TEXT_CONTENT_LENGTH) } : {}),
            ...(dataTestId ? { testId: dataTestId } : {}),
            ...(dataTelemetry ? { telemetryId: dataTelemetry } : {}),
            ...(href ? { href } : {}),
            ...(type ? { inputType: type } : {}),
          });
        } catch {
          // Swallow
        }
      };
      document.addEventListener('click', this.boundOnClick, { capture: true, passive: true });
    } catch (e) {
      console.warn('[Telemetry] setupClickTracking failed:', e);
    }
  }

  private findInteractiveElement(target: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = target;
    // Walk up the DOM tree (max 10 levels) to find an interactive element
    for (let i = 0; i < 10 && el; i++) {
      const tagName = el.tagName?.toLowerCase();
      if (!tagName) break;

      for (const selector of INTERACTIVE_SELECTORS) {
        try {
          if (el.matches(selector)) return el;
        } catch {
          // matches() can throw for invalid selectors in some browsers
        }
      }
      el = el.parentElement;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // 5. Form Submission Tracking
  // ---------------------------------------------------------------------------

  private setupFormTracking(): void {
    try {
      this.boundOnSubmit = (event: SubmitEvent) => {
        try {
          const form = event.target as HTMLFormElement;
          if (!form) return;

          // Capture field names (NOT values) and filter out sensitive fields
          const fieldNames: string[] = [];
          const elements = form.elements;
          for (let i = 0; i < elements.length; i++) {
            const el = elements[i] as HTMLInputElement;
            const name = el.name || el.id;
            if (name && !isSensitiveField(name)) {
              fieldNames.push(truncate(name, 50));
            }
          }

          this.trackEvent('interaction', 'form_submit', {
            formAction: truncate(form.action || '', MAX_STRING_LENGTH),
            formMethod: (form.method || 'GET').toUpperCase(),
            formId: form.id || undefined,
            formName: form.name || undefined,
            fieldNames: fieldNames.slice(0, 30), // Max 30 field names
            fieldCount: elements.length,
          });
        } catch {
          // Swallow
        }
      };
      document.addEventListener('submit', this.boundOnSubmit as EventListener, {
        capture: true,
        passive: true,
      });
    } catch (e) {
      console.warn('[Telemetry] setupFormTracking failed:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // 6. Performance Tracking
  // ---------------------------------------------------------------------------

  private setupPerformanceTracking(): void {
    try {
      // Long task observer
      if (typeof PerformanceObserver !== 'undefined') {
        try {
          this.performanceObserver = new PerformanceObserver((list) => {
            try {
              for (const entry of list.getEntries()) {
                if (entry.duration > LONG_TASK_THRESHOLD_MS) {
                  this.trackEvent('performance', 'long_task', {
                    duration: Math.round(entry.duration),
                    startTime: Math.round(entry.startTime),
                    name: truncate(entry.name, 100),
                    entryType: entry.entryType,
                  });
                }
              }
            } catch {
              // Swallow
            }
          });

          // PerformanceObserver for longtask may not be supported in all browsers
          this.performanceObserver.observe({ type: 'longtask', buffered: true });
        } catch {
          // longtask not supported, that's fine
          this.performanceObserver = null;
        }
      }
    } catch (e) {
      console.warn('[Telemetry] setupPerformanceTracking failed:', e);
    }
  }

  private capturePageLoadTimings(): void {
    try {
      const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (entries.length === 0) return;

      const nav = entries[0];

      const timings: PerformanceTimings = {
        dnsLookup: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
        tcpConnect: Math.round(nav.connectEnd - nav.connectStart),
        ttfb: Math.round(nav.responseStart - nav.requestStart),
        contentDownload: Math.round(nav.responseEnd - nav.responseStart),
        domInteractive: Math.round(nav.domInteractive - nav.fetchStart),
        domComplete: Math.round(nav.domComplete - nav.fetchStart),
        loadComplete: Math.round(nav.loadEventEnd - nav.fetchStart),
      };

      this.trackEvent('performance', 'page_load', {
        ...timings,
        transferSize: nav.transferSize,
        decodedBodySize: nav.decodedBodySize,
        navigationType: nav.type, // 'navigate', 'reload', 'back_forward', 'prerender'
      });
    } catch {
      // Not all browsers support PerformanceNavigationTiming
    }
  }

  // ---------------------------------------------------------------------------
  // 7. Session Tracking
  // ---------------------------------------------------------------------------

  private setupSessionTracking(): void {
    try {
      // Tab visibility changes
      this.boundOnVisibilityChange = () => {
        try {
          const state = document.visibilityState;
          this.trackEvent('session', 'visibility_change', {
            state,
          });
        } catch {
          // Swallow
        }
      };
      document.addEventListener('visibilitychange', this.boundOnVisibilityChange);

      // Online/offline status
      this.boundOnOnline = () => {
        try {
          this.trackEvent('session', 'online');
        } catch {
          // Swallow
        }
      };
      this.boundOnOffline = () => {
        try {
          this.trackEvent('session', 'offline');
        } catch {
          // Swallow
        }
      };
      window.addEventListener('online', this.boundOnOnline);
      window.addEventListener('offline', this.boundOnOffline);
    } catch (e) {
      console.warn('[Telemetry] setupSessionTracking failed:', e);
    }
  }

  private sendHeartbeat(): void {
    try {
      if (this.destroying) return;

      this.trackEvent('session', 'heartbeat', {
        tabVisible: document.visibilityState === 'visible',
        online: navigator.onLine,
        eventBufferSize: this.events.length,
        memoryUsage: this.getMemoryUsage(),
      });
    } catch {
      // Swallow
    }
  }

  private getMemoryUsage(): Record<string, number> | undefined {
    try {
      // performance.memory is a Chrome-only non-standard API
      const perfWithMemory = performance as Performance & {
        memory?: {
          usedJSHeapSize: number;
          totalJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      };
      if (perfWithMemory.memory) {
        return {
          usedJSHeapSize: perfWithMemory.memory.usedJSHeapSize,
          totalJSHeapSize: perfWithMemory.memory.totalJSHeapSize,
          jsHeapSizeLimit: perfWithMemory.memory.jsHeapSizeLimit,
        };
      }
    } catch {
      // Not available
    }
    return undefined;
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  destroy(): void {
    try {
      this.destroying = true;
      this.flush(); // Send any remaining events

      // Clear intervals
      if (this.flushInterval) {
        clearInterval(this.flushInterval);
        this.flushInterval = null;
      }
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }

      // Remove event listeners
      if (this.boundOnError) {
        window.removeEventListener('error', this.boundOnError);
      }
      if (this.boundOnUnhandledRejection) {
        window.removeEventListener('unhandledrejection', this.boundOnUnhandledRejection);
      }
      if (this.boundOnClick) {
        document.removeEventListener('click', this.boundOnClick, { capture: true } as EventListenerOptions);
      }
      if (this.boundOnSubmit) {
        document.removeEventListener('submit', this.boundOnSubmit as EventListener, {
          capture: true,
        } as EventListenerOptions);
      }
      if (this.boundOnPopState) {
        window.removeEventListener('popstate', this.boundOnPopState);
      }
      if (this.boundOnVisibilityChange) {
        document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
      }
      if (this.boundOnOnline) {
        window.removeEventListener('online', this.boundOnOnline);
      }
      if (this.boundOnOffline) {
        window.removeEventListener('offline', this.boundOnOffline);
      }
      if (this.boundOnBeforeUnload) {
        window.removeEventListener('beforeunload', this.boundOnBeforeUnload);
      }

      // Disconnect performance observer
      if (this.performanceObserver) {
        this.performanceObserver.disconnect();
        this.performanceObserver = null;
      }

      // Restore original pushState/replaceState
      if (this.originalPushState) {
        window.history.pushState = this.originalPushState;
      }
      if (this.originalReplaceState) {
        window.history.replaceState = this.originalReplaceState;
      }

      this.initialized = false;
    } catch (e) {
      console.warn('[Telemetry] destroy failed:', e);
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton Instance
// ---------------------------------------------------------------------------

export const telemetry = new Telemetry();

// ---------------------------------------------------------------------------
// Fetch Monkey-Patch for API Call Tracking
// ---------------------------------------------------------------------------
//
// We wrap the global fetch() to intercept API calls.
// This is done outside the class to avoid circular reference issues.
// Only API calls (matching the API base URL) are tracked.
// The telemetry endpoint itself is excluded to prevent infinite loops.
//
// ---------------------------------------------------------------------------

(function patchFetch() {
  try {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

    const originalFetch = window.fetch.bind(window);
    const apiBase = getApiBaseUrl();

    window.fetch = async function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      // Determine the URL string
      let url: string;
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else if (input instanceof Request) {
        url = input.url;
      } else {
        // Unknown type, pass through
        return originalFetch(input, init);
      }

      // Only track API calls (matching the API base URL)
      // Skip the telemetry endpoint itself to prevent infinite loops
      const isApiCall = url.startsWith(apiBase) || url.startsWith('/api');
      const isTelemetryCall = url.includes(TELEMETRY_PATH);

      if (!isApiCall || isTelemetryCall) {
        return originalFetch(input, init);
      }

      const method = (init?.method || 'GET').toUpperCase();
      const startTime = performance.now();

      try {
        const response = await originalFetch(input, init);
        const duration = performance.now() - startTime;

        telemetry.recordApiCall({
          method,
          url,
          status: response.status,
          duration,
          ok: response.ok,
          errorMessage: response.ok ? undefined : response.statusText,
        });

        return response;
      } catch (error) {
        const duration = performance.now() - startTime;

        telemetry.recordApiCall({
          method,
          url,
          status: 0,
          duration,
          ok: false,
          errorMessage: error instanceof Error ? error.message : 'Network error',
        });

        throw error;
      }
    };
  } catch {
    // If patching fails, fetch still works - just no tracking
    console.warn('[Telemetry] Failed to patch fetch for API tracking');
  }
})();

// ---------------------------------------------------------------------------
// React Hook
// ---------------------------------------------------------------------------

/**
 * React hook for manual telemetry tracking in components.
 *
 * Usage:
 *   const { trackEvent, trackError } = useTelemetry();
 *   trackEvent('feature', 'filter_applied', { stage: 'WON' });
 */
export function useTelemetry() {
  return {
    trackEvent: telemetry.trackEvent.bind(telemetry),
    trackError: telemetry.trackError.bind(telemetry),
  };
}

export default telemetry;
